import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { usd } from "../lib/format";
import { Icon } from "../components/icons";
import { Avatar, Empty } from "../components/ui";
import type { CsmScorecard } from "../lib/types";
import * as work from "../lib/workClient";

type SortKey = "bookArr" | "avgHealth" | "atRiskArr" | "renewalArr90" | "nps";
const SORTS: { key: SortKey; label: string }[] = [
  { key: "bookArr", label: "Book size" },
  { key: "avgHealth", label: "Avg health" },
  { key: "atRiskArr", label: "At-risk ARR" },
  { key: "renewalArr90", label: "Renewals (90d)" },
  { key: "nps", label: "NPS" },
];
const healthColor = (h: number) => (h >= 70 ? "var(--good)" : h >= 45 ? "var(--watch)" : "var(--risk)");
const npsColor = (n: number) => (n >= 30 ? "var(--good)" : n >= 0 ? "var(--watch)" : "var(--risk)");

export default function Scorecards() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [cards, setCards] = useState<CsmScorecard[]>([]);
  const [sort, setSort] = useState<SortKey>("bookArr");

  useEffect(() => { work.loadScorecards(ws).then((r) => { setMode(r.mode); setCards(r.scorecards); }); }, [ws]);

  const rows = useMemo(() => [...cards].sort((a, b) => {
    const av = a[sort] ?? -1, bv = b[sort] ?? -1;
    return (bv as number) - (av as number);
  }), [cards, sort]);

  const team = useMemo(() => {
    const bookArr = cards.reduce((n, c) => n + c.bookArr, 0);
    const atRiskArr = cards.reduce((n, c) => n + c.atRiskArr, 0);
    const accounts = cards.reduce((n, c) => n + c.accounts, 0);
    const overdue = cards.reduce((n, c) => n + c.overdueTasks, 0);
    const npsCards = cards.filter((c) => c.nps !== null);
    const nps = npsCards.length ? Math.round(npsCards.reduce((n, c) => n + (c.nps ?? 0), 0) / npsCards.length) : null;
    return { bookArr, atRiskArr, accounts, overdue, nps };
  }, [cards]);

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Team</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>CSM scorecards</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Every CSM's book at a glance — ARR, health mix, at-risk exposure, the renewal window, and NPS. Ranked so the manager sees where to lean in.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <ModePill mode={mode} />
          <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
            <span className="faint">Rank by</span>
            <select className="inp inp--sm" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="stats">
        <Stat icon="building" label="Managed accounts" value={String(team.accounts)} foot={`${cards.length} CSMs`} />
        <Stat icon="target" label="Book of business" value={usd(team.bookArr)} foot="Total managed ARR" />
        <Stat icon="bolt" label="At-risk ARR" value={usd(team.atRiskArr)} foot="Accounts in the risk band" color={team.atRiskArr ? "var(--risk)" : undefined} />
        <Stat icon="star" label="Team NPS" value={team.nps === null ? "—" : `${team.nps > 0 ? "+" : ""}${team.nps}`} foot="Avg across CSMs" color={team.nps === null ? undefined : npsColor(team.nps)} />
      </div>

      <div className="card">
        <div className="card__hd"><div><h3>By CSM</h3><span className="sub">{cards.length} owners · ranked by {SORTS.find((s) => s.key === sort)?.label.toLowerCase()}</span></div></div>
        <div style={{ overflowX: "auto" }}>
          {rows.length === 0 ? <div className="card__bd"><Empty>No CSMs with accounts yet.</Empty></div> : (
            <table className="table">
              <thead>
                <tr>
                  <th>CSM</th><th>Accounts</th><th>Book ARR</th><th>Health mix</th><th>Avg</th><th>At-risk</th><th>Renewals 90d</th><th>Open tasks</th><th>NPS</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.userId}>
                    <td>
                      <span className="row" style={{ gap: 9 }}>
                        <Avatar name={c.name} size="sm" />
                        <span><b style={{ fontSize: 13 }}>{c.name}</b><div className="faint" style={{ fontSize: 11.5 }}>{c.role}</div></span>
                      </span>
                    </td>
                    <td className="num">{c.accounts}</td>
                    <td className="num"><b>{usd(c.bookArr)}</b></td>
                    <td>
                      <span className="hbar" title={`${c.health.good} good · ${c.health.watch} watch · ${c.health.risk} risk`}>
                        {c.health.good > 0 && <span style={{ flex: c.health.good, background: "var(--good)" }} />}
                        {c.health.watch > 0 && <span style={{ flex: c.health.watch, background: "var(--watch)" }} />}
                        {c.health.risk > 0 && <span style={{ flex: c.health.risk, background: "var(--risk)" }} />}
                      </span>
                    </td>
                    <td className="num" style={{ color: healthColor(c.avgHealth), fontWeight: 600 }}>{c.avgHealth}</td>
                    <td className="num" style={{ color: c.atRiskArr ? "var(--risk)" : "var(--faint)" }}>{c.atRiskArr ? usd(c.atRiskArr) : "—"}</td>
                    <td>{c.renewals90 ? <span style={{ fontSize: 12.5 }}>{c.renewals90} · <span className="faint">{usd(c.renewalArr90)}</span></span> : <span className="faint">—</span>}</td>
                    <td>{c.openTasks ? <span style={{ fontSize: 12.5 }}>{c.openTasks}{c.overdueTasks ? <b style={{ color: "var(--risk)" }}> · {c.overdueTasks} late</b> : ""}</span> : <span className="faint">—</span>}</td>
                    <td>{c.nps === null ? <span className="faint">—</span> : <span className="badge" style={{ color: npsColor(c.nps), borderColor: "currentColor" }}>{c.nps > 0 ? "+" : ""}{c.nps}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color, fontSize: 24 }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
